import { isAdminMarketingUser } from "@redotech/admin-sdk/marketing/constants";
import { ORDER_TRACKING_BACKFILL_DAYS } from "@redotech/order-tracking-common/constants";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { Input, InputSchema, groupInput, input } from "@redotech/ui/form";
import { getDateTimeString } from "@redotech/util/date-format";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../../app/auth";
import { RedoAdminClientContext } from "../../../client/context";
import { isSyncInProgress, syncTrackers } from "../../../client/orders";
import { LoadTeamContext, TeamContext } from "../../team";

export const orderTrackingGeneralForm = groupInput({
  enabled: input<boolean>(),
  createTrackers: input<boolean>(),
  useRedoTrackingUrl: input<boolean>(),
  excludeTrackMyOrderClicks: input<boolean>(),
  smsEnabled: input<boolean>(),
  skipSmsOptInRequirement: input<boolean>(),
});

export type OrderTrackingGeneralForm = InputSchema.Input<
  typeof orderTrackingGeneralForm
>;

export type OrderTrackingGeneralValue = InputSchema.Value<
  typeof orderTrackingGeneralForm
>;

export const orderTrackingGeneralDefault: OrderTrackingGeneralValue = {
  enabled: false,
  createTrackers: false,
  useRedoTrackingUrl: false,
  excludeTrackMyOrderClicks: false,
  smsEnabled: false,
  skipSmsOptInRequirement: false,
};

export const OrderTrackingGeneralCard = memo(function OrderTrackingGeneralCard({
  input,
}: {
  input: OrderTrackingGeneralForm;
}) {
  const { enabled, useRedoTrackingUrl, excludeTrackMyOrderClicks } =
    input.inputs;

  const auth = useContext(AuthContext);
  const isAuthorized = isAdminMarketingUser(auth?.email);

  return (
    <Card title="Order Tracking">
      <section className={gridCss.grid}>
        <div className={gridCss.span6L}>
          <FormSwitch input={enabled} label="Order Tracking Enabled">
            Enable Order Tracking product
          </FormSwitch>
        </div>
        <div className={gridCss.span6L}>
          <FormSwitch
            disabled={!isAuthorized}
            input={excludeTrackMyOrderClicks}
            label="Exclude Track My Order Clicks"
          >
            Exclude "Track My Order" email clicks from upsell analytics
          </FormSwitch>
        </div>
        <div className={gridCss.span6L}>
          <FormSwitch
            disabled={!isAuthorized}
            input={useRedoTrackingUrl}
            label="Use Redo tracking URL"
          >
            <b>
              Do NOT enable unless the merchant is still sending tracking emails
              through Shopify.
            </b>{" "}
            If enabled, we will replace the tracking URL from the carrier with a
            deep link to Redo's tracking page
          </FormSwitch>
        </div>
        <Flex className={gridCss.span6L}>
          <FormSwitch
            disabled={!isAuthorized}
            input={input.inputs.smsEnabled}
            label="SMS"
          >
            Enable SMS/MMS sending for order tracking
          </FormSwitch>
        </Flex>
        <Flex className={gridCss.span6L}>
          <FormSwitch
            disabled={!isAuthorized}
            input={input.inputs.skipSmsOptInRequirement}
            label="Skip SMS Opt-In Requirement"
          >
            When enabled, transactional order tracking SMS messages will be sent
            to all customers unless they have explicitly unsubscribed. When
            disabled, customers must opt-in to receive transactional SMS.
          </FormSwitch>
        </Flex>
      </section>
    </Card>
  );
});

export const ShipmentTrackingCard = memo(function ShipmentTrackingCard({
  onAnalyzeTracking,
  createTrackersInput,
  orderTrackingEnabled,
}: {
  onAnalyzeTracking: () => void;
  createTrackersInput: Input<boolean>;
  orderTrackingEnabled: boolean;
}) {
  const team = useContext(TeamContext);
  const mostRecentSync = team?.settings.orderTracking?.mostRecentSync;

  useEffect(() => {
    if (orderTrackingEnabled) {
      createTrackersInput.setValue(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTrackingEnabled]);

  const canCreateTrackers =
    team?.settings?.orderTracking?.createTrackers && createTrackersInput.value;

  return (
    <Card title="Shipment tracking">
      <Flex dir="column" gap="lg">
        <FormSwitch input={createTrackersInput} label="Create trackers">
          Create trackers for all future orders. The last{" "}
          {ORDER_TRACKING_BACKFILL_DAYS} days will be synced within 2 hours.
        </FormSwitch>
        <Flex gap="md">
          <RedoButton
            hierarchy="secondary"
            onClick={onAnalyzeTracking}
            size="sm"
            text="Analyze tracking capability"
          />
          {canCreateTrackers && <ManualSyncTrackersButton />}
        </Flex>
        {canCreateTrackers && (
          <Flex dir="column" gap="xs">
            <Text fontWeight="medium">Tracker sync status</Text>
            <div>
              {mostRecentSync ? (
                <>
                  <Text fontSize="sm">
                    Report time:{" "}
                    {getDateTimeString(new Date(mostRecentSync.syncDate))}
                  </Text>
                  <Text fontSize="sm">
                    Orders fully synced (last {ORDER_TRACKING_BACKFILL_DAYS}{" "}
                    days): {mostRecentSync.ordersFullySynced}
                  </Text>
                  <Text fontSize="sm">
                    Orders not fully synced (last {ORDER_TRACKING_BACKFILL_DAYS}{" "}
                    days): {mostRecentSync.ordersNotFullySynced}
                  </Text>
                </>
              ) : (
                <Text fontSize="sm" textColor="secondary">
                  Never synced
                </Text>
              )}
            </div>
          </Flex>
        )}
      </Flex>
    </Card>
  );
});

const POLL_FREQUENCY_MS = 5000;

const ManualSyncTrackersButton = memo(function ManualSyncTrackersButton() {
  const team = useContext(TeamContext);
  const client = useContext(RedoAdminClientContext);
  const loadTeam = useContext(LoadTeamContext);

  const [syncInProgress, setSyncInProgress] = useState(false);
  const pollingInProgress = useRef(false);

  const pollUntilFinished: () => void = () => {
    if (pollingInProgress.current) {
      return;
    }
    pollingInProgress.current = true;
    setTimeout(refreshInProgressStatus, POLL_FREQUENCY_MS);
  };

  useEffect(() => {
    if (!client || !team) {
      return;
    }
    isSyncInProgress(client, { teamId: team?._id })
      .then((inProgress) => {
        setSyncInProgress(inProgress);
        if (inProgress) {
          pollUntilFinished();
        }
      })
      .catch(() => console.error("Failed to check sync status"));
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  const [_, doStartSync] = useTriggerLoad(async (signal) => {
    if (!client || !team) {
      return;
    }
    setSyncInProgress(true);
    await syncTrackers(client, { teamId: team._id, signal });
    pollUntilFinished();
  });

  if (!team || !client) {
    return null;
  }

  const refreshInProgressStatus = async () => {
    const inProgress = await isSyncInProgress(client, { teamId: team._id });
    setSyncInProgress(inProgress);
    if (inProgress) {
      setTimeout(refreshInProgressStatus, POLL_FREQUENCY_MS);
    } else {
      pollingInProgress.current = false;
      loadTeam?.();
    }
  };

  return (
    <>
      <RedoButton
        disabled={syncInProgress}
        hierarchy="primary"
        onClick={() => doStartSync()}
        pending={syncInProgress}
        size="sm"
        text="Sync unsynced trackers"
      />
      {syncInProgress && (
        <Text fontSize="sm" textColor="warning">
          Sync in progress
        </Text>
      )}
    </>
  );
});
